// components/FormularSesizare/steps/Step1DateIdentificare.jsx

import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import roRO from "date-fns/locale/ro";

// Soft UI Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

import CharacterCounter from "../CharacterCounter";
import {
  FIELD_LIMITS,
  createSanitizedChangeHandler,
  getMaxBirthDate,
  getMinBirthDate,
  sanitizeAdresa,
  sanitizeNume,
  sanitizeTelefon,
} from "../validation";

function Step1DateIdentificare({ formData, handleChange, handleDateNastereChange, customColors, errors = {} }) {
  const handleSanitizedChange = (sanitize) => createSanitizedChangeHandler(handleChange, sanitize);

  const maxBirthDate = React.useMemo(() => getMaxBirthDate(), []);
  const minBirthDate = React.useMemo(() => getMinBirthDate(), []);

  return (
<SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="bold" mb={3} color={customColors.primary.main}>
              Date de identificare ale petentului
            </SoftTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.nume ? "error" : customColors.secondary.main}>
                      Nume și prenume*
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput 
                    placeholder="Introduceți numele complet" 
                    name="nume"
                    value={formData.nume}
                    onChange={handleSanitizedChange(sanitizeNume)}
                    required
                    error={!!errors.nume}
                    inputProps={{ maxLength: FIELD_LIMITS.nume }}
                    icon={{
                      component: "person",
                      direction: "left"
                    }}
                  />
                  {errors.nume && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.nume}
                    </SoftTypography>
                  )}
                  <CharacterCounter value={formData.nume} limit={FIELD_LIMITS.nume} />
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.email ? "error" : customColors.secondary.main}>
                      Adresă de email*
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput 
                    type="email" 
                    placeholder="exemplu@email.com" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    error={!!errors.email}
                    icon={{
                      component: "email",
                      direction: "left"
                    }}
                  />
                  {errors.email && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.email}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.telefon ? "error" : customColors.secondary.main}>
                      Număr de telefon*
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput 
                    placeholder="07xxxxxxxx" 
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleSanitizedChange(sanitizeTelefon)}
                    required
                    error={!!errors.telefon}
                    inputProps={{
                      maxLength: FIELD_LIMITS.telefon,
                      inputMode: "numeric",
                      autoComplete: "tel",
                    }}
                    icon={{
                      component: "phone",
                      direction: "left"
                    }}
                  />
                  {errors.telefon && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.telefon}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.dataNasterii ? "error" : customColors.secondary.main}>
                      Data nașterii*
                    </SoftTypography>
                  </SoftBox>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={roRO}>
                    <DatePicker
                      value={formData.dataNasterii}
                      onChange={handleDateNastereChange}
                      maxDate={maxBirthDate}
                      minDate={minBirthDate}
                      disableFuture
                      openTo="year"
                      views={['year', 'month', 'day']}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          error={!!errors.dataNasterii}
                          placeholder="ZZ/LL/AAAA"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '45px',
                              borderRadius: '0.5rem',
                              '& fieldset': {
                                borderColor: errors.dataNasterii ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
                              },
                              '&:hover fieldset': {
                                borderColor: errors.dataNasterii ? '#f44336' : customColors.secondary.main,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errors.dataNasterii ? '#f44336' : customColors.primary.main,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  {errors.dataNasterii && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.dataNasterii}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12}>
                <SoftBox mb={2}>
                  <SoftBox mb={1} ml={0.5}>
                    <SoftTypography component="label" variant="caption" fontWeight="bold" color={errors.adresa ? "error" : customColors.secondary.main}>
                      Adresă fizică*
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput 
                    placeholder="Strada, număr, localitate, județ" 
                    multiline
                    rows={3}
                    name="adresa"
                    value={formData.adresa}
                    onChange={handleSanitizedChange(sanitizeAdresa)}
                    required
                    error={!!errors.adresa}
                    inputProps={{ maxLength: FIELD_LIMITS.adresa }}
                    icon={{
                      component: "location_on",
                      direction: "left"
                    }}
                  />
                  {errors.adresa && (
                    <SoftTypography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                      {errors.adresa}
                    </SoftTypography>
                  )}
                  <CharacterCounter value={formData.adresa} limit={FIELD_LIMITS.adresa} />
                </SoftBox>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.confidential}
                      onChange={handleChange}
                      name="confidential"
                      sx={{ color: customColors.primary.main, '&.Mui-checked': { color: customColors.primary.main } }}
                    />
                  }
                  label={
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      Solicit menținerea datelor mele confidențiale
                    </SoftTypography>
                  }
                />
              </Grid>
            </Grid>
          </SoftBox>
  );
}

Step1DateIdentificare.propTypes = {
    formData: PropTypes.shape({
      nume: PropTypes.string,
      email: PropTypes.string,
      telefon: PropTypes.string,
      adresa: PropTypes.string,
      dataNasterii: PropTypes.instanceOf(Date),
      confidential: PropTypes.bool,
    }).isRequired,
    handleChange: PropTypes.func.isRequired,
    handleDateNastereChange: PropTypes.func.isRequired,
    customColors: PropTypes.shape({
      primary: PropTypes.shape({
        main: PropTypes.string,
      }),
      secondary: PropTypes.shape({
        main: PropTypes.string,
      }),
    }).isRequired,
    errors: PropTypes.object,
  };

export default Step1DateIdentificare;
